import { Component, type ReactNode } from 'react';
import { Button, Card } from '@heroui/react';
import { RefreshIcon } from './icons.tsx';

/**
 * The one place a failed `lazy()` import is caught.
 *
 * ⚠️ A <Suspense> DOES NOT CATCH A REJECTED CHUNK, and splitting the admin
 * pages is what made that reachable. The failure has a specific and ordinary
 * cause: a deploy replaces the hashed assets, and any admin whose tab was open
 * before it still holds a router pointing at
 * `/assets/AiUsagePage-COChNua0.js` — a file that no longer exists. Clicking
 * Расход ИИ then throws, and without this the whole app is replaced by React
 * Router's own error screen, which is a stack trace in English on a Russian
 * page nobody can act on.
 *
 * Reloading genuinely fixes it — the fresh document asks for the new hashes —
 * so the recovery is one button, and it says why.
 *
 * A class component because that is still the only way to implement
 * `getDerivedStateFromError`; there is no hook equivalent.
 */
export class ChunkErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override render() {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="mx-auto flex w-full max-w-md flex-col gap-4 py-10">
        <Card>
          <Card.Header>
            <Card.Title>Страница не загрузилась</Card.Title>
          </Card.Header>
          <Card.Content className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted">
              Скорее всего, приложение обновилось, пока вкладка была открыта. Перезагрузка это
              чинит.
            </p>
            {/* A plain `Button`, not an `ActionButton`: there is nothing to
                wait for. `reload()` replaces the document, so a pending state
                would be painted onto a page that is already going away. */}
            <Button onPress={() => window.location.reload()}>
              <RefreshIcon aria-hidden="true" />
              Перезагрузить
            </Button>
          </Card.Content>
        </Card>
      </div>
    );
  }
}
